import { useEffect, useState } from "react";
import { Download, FileText, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { PartnerShell } from "./Dashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

function BrandingChip({ mode, reason, partnerName }) {
  if (mode === "partner_white_label") {
    return (
      <span
        data-testid="partner-report-branding-chip-partner"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800"
        title={partnerName ? `White-labelled as ${partnerName}` : "Partner white-label"}
      >
        <ShieldCheck className="w-3 h-3" /> Your branding
      </span>
    );
  }
  // Aware fallback — surface the reason so partner can self-diagnose.
  return (
    <span
      data-testid="partner-report-branding-chip-scwt"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700"
      title={reason ? `Aware branding — fallback reason: ${reason}` : "Aware branding"}
    >
      <AlertTriangle className="w-3 h-3" /> Aware branding
    </span>
  );
}

export default function PartnerReports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/partner/reports");
        if (!cancelled) setRows(data.rows || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Unable to load reports");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadUrl = (reference, kind = "summary") =>
    `${BACKEND_URL}/api/partner/reports/${encodeURIComponent(reference)}/${kind === "coa" ? "coa" : "summary"}.pdf`;

  const onDownload = async (reference, kind = "summary") => {
    const token = localStorage.getItem("scwt_token");
    try {
      const res = await fetch(downloadUrl(reference, kind), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "coa" ? `aware-full-analysis-${reference}.pdf` : `summary-${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(e?.message || "Unable to download report");
    }
  };

  return (
    <PartnerShell active="reports">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="partner-reports-heading">
          Customer Reports
        </h1>
        <p className="text-sm text-slate-600 mt-1" data-testid="partner-reports-subheading">
          Download the customer-facing Summary Report or the full AWARE laboratory
          analysis (Certificate of Analysis) for any of your published bookings.
        </p>
      </div>

      {loading && (
        <div
          className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-500"
          data-testid="partner-reports-loading"
        >
          Loading reports…
        </div>
      )}
      {error && !loading && (
        <div
          className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-rose-800"
          data-testid="partner-reports-error"
        >
          {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div
          className="rounded-xl bg-white border border-slate-200 p-10 text-center"
          data-testid="partner-reports-empty"
        >
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-600">
            No published reports yet. Once a booking attributed to you reaches published status,
            its Summary Report will appear here.
          </div>
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="partner-reports-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-bold">Booking</th>
                <th className="text-left px-4 py-3 font-bold">Customer</th>
                <th className="text-left px-4 py-3 font-bold">Test</th>
                <th className="text-left px-4 py-3 font-bold">Published</th>
                <th className="text-left px-4 py-3 font-bold">Branding</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const b = row.booking || {};
                const t = row.test || {};
                const m = row.report_meta || {};
                return (
                  <tr
                    key={b.reference || i}
                    className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                    data-testid={`partner-reports-row-${b.reference}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-slate-900" data-testid="partner-reports-row-reference">
                        {b.reference}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {b.booking_source || "direct"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900">{b.customer?.name || "—"}</div>
                      <div className="text-[10px] text-slate-500">{b.customer?.suburb || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{t.test_name || t.test_id || "—"}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{t.sample_number || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {fmtDate(t.published_at || t.collected_at)}
                    </td>
                    <td className="px-4 py-3">
                      <BrandingChip
                        mode={m.summary_branding_mode}
                        reason={m.white_label_fallback_reason}
                        partnerName={m.summary_partner_name}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => onDownload(b.reference)}
                          data-testid={`partner-reports-download-${b.reference}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" /> Summary PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => onDownload(b.reference, "coa")}
                          data-testid={`partner-reports-download-coa-${b.reference}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" /> Full Analysis (COA)
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div
        className="mt-6 text-xs text-slate-500"
        data-testid="partner-reports-disclaimer"
      >
        The Summary Report is your customer-facing action plan.
        The Full Analysis (Certificate of Analysis) is the complete AWARE-branded
        laboratory report — the same current approved version the customer receives.
      </div>
    </PartnerShell>
  );
}
